/**
 * =============================================================================
 * IDS CRM Web - Work-log Outcome section (2026-08-12)
 * =============================================================================
 *
 * ONE close/handover mechanic for reactive tickets AND scheduled work
 * (Gareth: identical sign-off both sides; the two modals drifting apart is
 * how the van check went missing on one of them).
 *
 * Closing asks for the summary and the "fault found — raise a follow-up
 * ticket" checkbox (customer satisfied, typed duration and health score are
 * gone — durations derive from Job Stages). A handover completes THIS log and
 * passes the baton: assign-next plus the notes, which are compulsory — the
 * notes ARE the handover.
 *
 * Fault codes are NOT here: they belong to the work log itself and are
 * recorded in the modal above, at any work type and any point in the job
 * (Gareth 2026-09-11).
 *
 * The parent owns state; this renders and patches via onChange(partial).
 * Value keys: outcome, summary, follow_up_ticket_required, follow_up_subject,
 * follow_up_description, follow_up_priority, assign_next_type,
 * assigned_to_next_id, follow_up_asset_id.
 *
 * Jobs with several robots (multi-asset plan, Phase 3, "THE WRONG ROBOT
 * GETTING STAMPED"): the parent passes `robots` [{ asset_id, asset_ref }],
 * primary first, and a follow-up ticket then asks "Which robot?" so the
 * ticket lands on the right one. Tickets pass nothing and see no change.
 *
 * Phase 6 step 4: a job with several robots raises a follow-up ticket per
 * robot, in each robot's own section (TaskAssetCheckSection). The parent then
 * passes `followUpPerRobot` and the job-level box and "Which robot?" are not
 * shown. It defaults to false, so tickets and one-robot jobs are unchanged.
 *
 * Phase 6 step 7 (saved Log Work, spec 6.4 and 6.5): a job passes
 * `offerCarryOn` and "Assigned to next" then also offers Carry on tomorrow
 * (assign_next_type 'carry_on'): today's hours are saved, the engineer keeps
 * the job and nothing about it changes. It is offered only with an outcome
 * that does not close the job, and choosing a closing outcome afterwards
 * takes it off again. Off by default, so tickets never see it.
 * =============================================================================
 */

import React from 'react';

export const CARRY_ON_LABEL = 'Carry on tomorrow (you keep the job; nothing about it changes)';
export const CARRY_ON_NOTES_NEEDED = 'Write what was done today in Notes before choosing Carry on tomorrow.';

export const outcomeClosesWork = (config, outcomeValue) => {
  const selected = config.outcomes?.find(outcome => outcome.value === outcomeValue);
  return Boolean(selected
    ? selected.closes_work
    : ['resolved', 'completed', 'complete'].includes(outcomeValue));
};

function WorkLogOutcomeSection({
  config,
  engineers,
  value,
  onChange,
  summaryLabel = 'Completion summary',
  summaryPlaceholder = 'Summary of the work completed and any observations.',
  followUpPlaceholder = 'Fault found during this job',
  notesProvided = false,
  groupLabel = 'Maintenance Planning Owner group',
  // Reactive tickets may save an in-progress log with no outcome yet.
  allowInProgress = false,
  // Reactive non-site logs show the finish-time input beside the outcome.
  finishTimeSlot = null,
  closesLabel = 'Closes the job',
  // The robots on the job, primary first; a follow-up asks which one when
  // there is more than one.
  robots = [],
  // Follow-up tickets are raised on each robot instead (Phase 6 step 4): the
  // job-level box is hidden. Off unless the parent asks for it.
  followUpPerRobot = false,
  // Jobs only (saved Log Work): offer Carry on tomorrow under "Assigned to
  // next" when the outcome does not close the job.
  offerCarryOn = false
}) {
  const closesWork = outcomeClosesWork(config, value.outcome);
  const hasOutcome = Boolean(value.outcome);
  const robotChoices = (Array.isArray(robots) ? robots : []).filter(robot => robot && robot.asset_id);
  const asksWhichRobot = robotChoices.length > 1;
  const followUpAssetId = value.follow_up_asset_id || (asksWhichRobot ? robotChoices[0].asset_id : '');
  const closingOutcomes = (config.outcomes || []).filter(outcome => outcome.closes_work);
  const handoverOutcomes = (config.outcomes || []).filter(outcome => !outcome.closes_work);

  const patch = (fields) => onChange(fields);

  const outcomeSelect = (
    <div className="form-group">
      <label htmlFor="outcome">Outcome {allowInProgress ? '' : '*'}</label>
      <select
        id="outcome"
        name="outcome"
        value={value.outcome}
        onChange={e => patch({
          outcome: e.target.value,
          // Carry on tomorrow keeps the job open, so a closing outcome takes it off.
          ...(value.assign_next_type === 'carry_on' && outcomeClosesWork(config, e.target.value)
            ? { assign_next_type: '' }
            : {})
        })}
        required={!allowInProgress}
      >
        <option value="">{allowInProgress ? 'In Progress (no outcome yet)' : 'Select outcome...'}</option>
        <optgroup label={closesLabel}>
          {closingOutcomes.map(outcome => (
            <option key={outcome.value} value={outcome.value}>{outcome.label}</option>
          ))}
        </optgroup>
        <optgroup label="Hands over">
          {handoverOutcomes.map(outcome => (
            <option key={outcome.value} value={outcome.value}>{outcome.label}</option>
          ))}
        </optgroup>
      </select>
    </div>
  );

  return (
    <>
      {finishTimeSlot
        ? <div className="form-row">{outcomeSelect}{finishTimeSlot}</div>
        : outcomeSelect}

      {hasOutcome && closesWork && (
        <div className="handover-panel">
          <div className="form-group">
            <label>{summaryLabel} *</label>
            <textarea
              value={value.summary}
              onChange={e => patch({ summary: e.target.value })}
              rows={3}
              required
              placeholder={summaryPlaceholder}
            />
          </div>
          {!followUpPerRobot && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={value.follow_up_ticket_required}
                  onChange={e => patch({ follow_up_ticket_required: e.target.checked })}
                />
                Fault found — raise a follow-up ticket
              </label>
            </div>
          )}
          {!followUpPerRobot && value.follow_up_ticket_required && (
            <>
              {asksWhichRobot && (
                <div className="form-group">
                  <label htmlFor="follow_up_asset_id">Which robot? *</label>
                  <select
                    id="follow_up_asset_id"
                    value={followUpAssetId}
                    onChange={e => patch({ follow_up_asset_id: e.target.value })}
                    required
                  >
                    {robotChoices.map(robot => (
                      <option key={robot.asset_id} value={robot.asset_id}>{robot.asset_ref || robot.asset_id}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Follow-up subject</label>
                <input
                  type="text"
                  value={value.follow_up_subject}
                  onChange={e => patch({ follow_up_subject: e.target.value })}
                  placeholder={followUpPlaceholder}
                />
              </div>
              <div className="form-group">
                <label>Follow-up description</label>
                <textarea
                  value={value.follow_up_description}
                  onChange={e => patch({ follow_up_description: e.target.value })}
                  rows={2}
                  placeholder="What needs looking at?"
                />
              </div>
              <div className="form-group">
                <label>Follow-up priority</label>
                <select
                  value={value.follow_up_priority}
                  onChange={e => patch({ follow_up_priority: e.target.value })}
                >
                  <option value="">Same as this job</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </>
          )}
        </div>
      )}

      {hasOutcome && !closesWork && (
        <div className="handover-panel">
          <div className="form-group">
            <label>Assigned to next *</label>
            <select
              value={value.assign_next_type}
              onChange={e => patch({
                assign_next_type: e.target.value,
                ...(e.target.value !== 'user' ? { assigned_to_next_id: '' } : {})
              })}
              required
            >
              <option value="">Select next owner...</option>
              <option value="self">Same engineer</option>
              <option value="user">Another engineer</option>
              <option value="group">{groupLabel}</option>
              {offerCarryOn && <option value="carry_on">{CARRY_ON_LABEL}</option>}
            </select>
          </div>
          {value.assign_next_type === 'user' && (
            <div className="form-group">
              <label>Next engineer *</label>
              <select
                value={value.assigned_to_next_id}
                onChange={e => patch({ assigned_to_next_id: e.target.value })}
                required
              >
                <option value="">Select engineer...</option>
                {engineers.map(engineer => (
                  <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
                ))}
              </select>
            </div>
          )}
          {offerCarryOn && value.assign_next_type === 'carry_on' ? (
            <div className={`modal-hint ${notesProvided ? '' : 'warning'}`}>
              {notesProvided
                ? 'Today\'s hours are saved with the Notes above. You keep the job, and your robots\' answers wait for tomorrow in Continue Log Work.'
                : CARRY_ON_NOTES_NEEDED}
            </div>
          ) : (
            <div className={`modal-hint ${notesProvided ? '' : 'warning'}`}>
              {notesProvided
                ? 'The Notes above go to the next owner as the handover notes.'
                : 'Handover notes are required — write what the next owner needs to know in the Notes box above.'}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default WorkLogOutcomeSection;
